import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import type { Sessao } from '../../auth';
import {
  conferirPrateleira,
  contarEstoque,
  lerFila,
  lerInvestigacao,
  naoConsegui,
} from '../../auditoria';
import type {
  CasoDaFila,
  Investigacao,
  LocalDaConferencia,
  MotivoSemVerificacao,
  OrdemDaFila,
} from '../../auditoria';
import { ROTULO_SEM_VERIFICACAO } from '../../auditoria';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { Escrita } from '../../giro';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import { Laboratorio, novaChave, papeisDe, quandoBR, ultimaCentral, unidades } from './comum';
import { ResumoDaDivergencia } from './ResumoDaDivergencia';
import { fraseDaReconferencia } from './resumo-da-conferencia';
import './divergencias.css';

/**
 * CONFERÊNCIA RÁPIDA (dono, 13/10/2026): "abrir, contar, salvar e passar para o próximo".
 *
 *   PRATELEIRA (Igor): quantas unidades estão na gôndola.
 *   ESTOQUE (Felipe): quantas unidades existem no estoque inteiro, neste local.
 *
 * Sem linha do tempo, sem ajuste, sem decisão. Campo vazio NÃO é zero. Cada salvar leva uma chave
 * (clique duplo e nova tentativa não duplicam) e o servidor grava junto o saldo do HOS da hora.
 * Os dois locais são contados ao mesmo tempo sem um sobrescrever o outro.
 */

const TITULO: Readonly<Record<LocalDaConferencia, string>> = {
  PRATELEIRA: 'Conferência da Prateleira',
  ESTOQUE: 'Conferência do Estoque',
};
const PERGUNTA: Readonly<Record<LocalDaConferencia, string>> = {
  PRATELEIRA: 'Quantas unidades você encontrou na prateleira?',
  ESTOQUE: 'Quantas unidades existem no estoque físico?',
};
const DICA: Readonly<Record<LocalDaConferencia, string>> = {
  PRATELEIRA: 'Conte só o que está exposto na área de vendas. Nenhuma? Digite 0.',
  ESTOQUE:
    'Conte todas as unidades deste produto neste local — não só as que faltaram na reposição. Nenhuma? Digite 0.',
};
const ORDENS: readonly OrdemDaFila[] = [
  'ROTA',
  'PRIORIDADE',
  'DIFERENCA',
  'ANTIGA',
  'CATEGORIA',
  'AZ',
];

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'erro'; readonly falha: Falha }
  | { readonly fase: 'pronto' };

export function ConferenciaRapida({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
}) {
  const papeis = papeisDe(eu);
  const local: LocalDaConferencia =
    parametros.get('local') === 'ESTOQUE' ? 'ESTOQUE' : 'PRATELEIRA';
  const inicio = parametros.get('inicio');
  const setor = parametros.get('setor');
  const busca = parametros.get('busca');
  // Os filtros da Central viajam junto: o "próximo" respeita grupo, balconista e período.
  const grupo = parametros.get('grupo');
  const pessoa = parametros.get('pessoa');
  const de = parametros.get('de');
  const ate = parametros.get('ate');
  const ordemPedida = parametros.get('ordem') as OrdemDaFila | null;
  const ordem: OrdemDaFila = ordemPedida && ORDENS.includes(ordemPedida) ? ordemPedida : 'ROTA';
  const voltar = parametros.get('volta') ?? ultimaCentral();
  const pode = local === 'PRATELEIRA' ? papeis.prateleira : papeis.estoque;

  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [fila, setFila] = useState<CasoDaFila[]>([]);
  const [total, setTotal] = useState(0);
  const [feitos, setFeitos] = useState(0);
  const [valor, setValor] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [incerto, setIncerto] = useState(false);
  const [salvo, setSalvo] = useState<string | null>(null);
  const [semVerificar, setSemVerificar] = useState(false);
  const [motivo, setMotivo] = useState<MotivoSemVerificacao | null>(null);
  const [obs, setObs] = useState('');
  /** Casos já resolvidos nesta sessão (não voltam quando a fila é relida). */
  const resolvidos = useRef(new Set<string>());
  /** A chave da ação atual: muda quando muda o caso; repete na nova tentativa do mesmo clique. */
  const chave = useRef(novaChave());
  const campo = useRef<HTMLInputElement>(null);
  /**
   * Resumo da prateleira (14/10): a investigação (saldo ao vivo + razão do HOS) de cada caso, lida
   * UMA vez por caso (o atual e o próximo, antecipado). Ausente = buscando; null = não veio.
   */
  const [investigacoes, setInvestigacoes] = useState<Record<string, Investigacao | null>>({});
  const pedidas = useRef(new Set<string>());
  const investigar = useCallback(async (id: string, deNovo = false) => {
    if (pedidas.current.has(id) && !deNovo) return;
    pedidas.current.add(id);
    if (deNovo)
      setInvestigacoes((m) => {
        const { [id]: _, ...resto } = m;
        return resto;
      });
    const r = await lerInvestigacao(id);
    setInvestigacoes((m) => ({ ...m, [id]: r.ok ? r.dados : null }));
  }, []);

  const carregar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setEstado({ fase: 'carregando' });
      const r = await lerFila({
        conferir: local,
        setor,
        grupo,
        pessoa,
        busca,
        ordem,
        limite: 200,
        ...(de ? { de, ate: ate ?? de } : {}),
      });
      if (!r.ok) {
        if (!silencioso) setEstado({ fase: 'erro', falha: r.falha });
        return;
      }
      const lista = r.dados.casos.filter((c) => !resolvidos.current.has(c.id));
      setFila((atual) => {
        // A fila relida não tira da frente o produto que a pessoa está contando.
        const frente = silencioso ? atual[0] : undefined;
        let nova = frente ? [frente, ...lista.filter((c) => c.id !== frente.id)] : lista;
        if (!silencioso && inicio) {
          const i = nova.findIndex((c) => c.id === inicio);
          if (i > 0) nova = [nova[i] as CasoDaFila, ...nova.slice(0, i), ...nova.slice(i + 1)];
        }
        return nova;
      });
      setTotal(r.dados.total);
      setEstado({ fase: 'pronto' });
    },
    [local, setor, grupo, pessoa, de, ate, busca, ordem, inicio],
  );

  useEffect(() => {
    if (pode) void carregar();
  }, [carregar, pode]);

  // Relê a fila de tempos em tempos (casos novos entram no fim), só com a tela visível.
  useEffect(() => {
    if (!pode) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar(true);
    }, 60_000);
    return () => window.clearInterval(t);
  }, [carregar, pode]);

  const atual = fila[0] ?? null;
  const proximo = fila[1] ?? null;

  // O resumo do produto da frente; o do próximo vem logo depois (a troca já abre completa).
  useEffect(() => {
    if (!atual) return;
    void investigar(atual.id).then(() => {
      if (proximo) void investigar(proximo.id);
    });
  }, [atual?.id, proximo?.id, investigar]);

  // Pré-carrega a foto do próximo produto: a troca é instantânea.
  useEffect(() => {
    if (proximo?.fotoProduto) {
      const img = new Image();
      img.src = proximo.fotoProduto;
    }
  }, [proximo?.fotoProduto]);

  // Caso novo na frente: chave nova, campo limpo e com o foco.
  useEffect(() => {
    chave.current = novaChave();
    setValor('');
    setErro(null);
    setIncerto(false);
    setSemVerificar(false);
    setMotivo(null);
    setObs('');
    // Produto novo começa do topo: a confirmação do anterior e o resumo do novo ficam à vista.
    if (window.scrollY > 0) window.scrollTo({ top: 0 });
    window.setTimeout(() => campo.current?.focus({ preventScroll: true }), 30);
  }, [atual?.id]);

  // A confirmação some sozinha.
  useEffect(() => {
    if (!salvo) return;
    const t = window.setTimeout(() => setSalvo(null), 2_600);
    return () => window.clearTimeout(t);
  }, [salvo]);

  const avancar = (texto: string) => {
    if (!atual) return;
    resolvidos.current.add(atual.id);
    setFeitos((n) => n + 1);
    setSalvo(texto);
    setFila((f) => f.slice(1));
    setTotal((t) => Math.max(0, t - 1));
    if (fila.length <= 3) void carregar(true);
  };

  /** Os três desfechos: confirmada avança; incerta pede nova tentativa (mesma chave); recusada explica. */
  const tratar = (r: Escrita<unknown>, texto: string) => {
    if (r.tipo === 'confirmada') {
      // O Igor filtra (dono, 19/10): o estoque tinha o que faltou → o caso fechou sem ir ao Felipe.
      const fechou = (r.dados as { etapa?: string } | null)?.etapa === 'CONCLUIDA';
      return avancar(
        fechou ? `${texto} · O estoque tinha: erro do balconista confirmado, caso fechado.` : texto,
      );
    }
    if (r.tipo === 'incerta') {
      setIncerto(true);
      return;
    }
    // Caso que saiu da fila (concluído, contado por outra pessoa): segue para o próximo.
    if (r.status === 409 || r.status === 404) {
      setErro(`${r.mensagem} Seguindo para o próximo.`);
      if (atual) resolvidos.current.add(atual.id);
      window.setTimeout(() => setFila((f) => f.slice(1)), 1_400);
      return;
    }
    setErro(r.mensagem);
  };

  const quantidade = valor.trim() === '' ? null : Number(valor);
  // Dono (14/10): HOS acima de 6 → o estoque é a BUSCA do que faltou (de 0 até o alvo).
  const porBusca = local === 'ESTOQUE' && atual?.modoEstoque === 'BUSCA' && (atual.alvo ?? 0) >= 1;
  const maximo = porBusca && atual ? atual.alvo : 99_999;
  const valido =
    quantidade !== null && Number.isInteger(quantidade) && quantidade >= 0 && quantidade <= maximo;

  /** Botão "Tem mais de 12" (Carlos, 14/10): sem contar, vale como "o estoque tinha". */
  const maisDe12 = async () => {
    if (!atual || enviando) return;
    setEnviando(true);
    setErro(null);
    setIncerto(false);
    const r = await contarEstoque(sessao, atual.id, { chave: chave.current, maisDe12: true });
    setEnviando(false);
    tratar(r, `Salvo: mais de 12 no estoque · ${atual.produto}`);
  };

  const salvar = async () => {
    if (!atual || !valido || enviando || quantidade === null) return;
    setEnviando(true);
    setErro(null);
    setIncerto(false);
    const r =
      local === 'PRATELEIRA'
        ? await conferirPrateleira(sessao, atual.id, {
            versao: atual.versao,
            chave: chave.current,
            encontrado: quantidade,
            situacao: quantidade === 0 ? 'SEM_PRODUTO' : 'NA_PRATELEIRA',
            liberar: true,
          })
        : porBusca
          ? await contarEstoque(sessao, atual.id, {
              chave: chave.current,
              localizado: quantidade,
            })
          : await contarEstoque(sessao, atual.id, { chave: chave.current, total: quantidade });
    setEnviando(false);
    tratar(
      r,
      local === 'PRATELEIRA'
        ? `Salvo: ${atual.produto}. ${fraseDaReconferencia(atual.prateleira, quantidade)}`
        : porBusca
          ? `Salvo: achou ${String(quantidade)} de ${String(atual.alvo)} · ${atual.produto}`
          : `Salvo: ${unidades(quantidade)} · ${atual.produto}`,
    );
  };

  const registrarSemVerificar = async () => {
    if (!atual || !motivo || enviando) return;
    if (motivo === 'OUTRO' && obs.trim().length < 3) {
      setErro('Conte em poucas palavras o que aconteceu.');
      return;
    }
    setEnviando(true);
    setErro(null);
    setIncerto(false);
    const r = await naoConsegui(sessao, atual.id, {
      chave: chave.current,
      local,
      motivo,
      observacao: obs.trim() || null,
    });
    setEnviando(false);
    tratar(r, `Registrado: não verificado (${ROTULO_SEM_VERIFICACAO[motivo].toLowerCase()})`);
  };

  const posicao = feitos + 1;
  const tamanho = useMemo(
    () => feitos + Math.max(total, fila.length),
    [total, feitos, fila.length],
  );

  if (!pode) return <SemAcesso area={TITULO[local]} />;
  if (estado.fase === 'erro')
    return (
      <section className="dp dp-conf">
        <ErroDeArea
          oQue={estado.falha.tipo === 'offline' ? 'a fila (sem conexão)' : 'a fila'}
          aoTentar={() => void carregar()}
        />
      </section>
    );

  return (
    <section className="dp dp-conf" aria-busy={estado.fase === 'carregando'}>
      <div className="dp-conf-topo">
        <a className="dp-link" href={voltar}>
          <Icone nome="arrow-l" tamanho={16} />
          Voltar
        </a>
        {atual && (
          <span className="dp-conf-pos" aria-live="polite">
            Produto {posicao} de {tamanho}
          </span>
        )}
      </div>
      <div>
        <h1 className="dp-conf-titulo">{TITULO[local]}</h1>
        <div className="dp-barra" role="presentation" style={{ marginTop: 10 }}>
          <span style={{ width: `${tamanho ? Math.round((feitos / tamanho) * 100) : 0}%` }} />
        </div>
      </div>

      {salvo && (
        <div className="dp-salvo" role="status">
          <Icone nome="check" tamanho={16} traco={2} />
          {salvo}
        </div>
      )}

      {estado.fase === 'carregando' && !atual ? (
        <div className="dp-produto" aria-hidden="true">
          <p className="dp-dica">Carregando a fila…</p>
        </div>
      ) : !atual ? (
        <EstadoVazio
          icone="check"
          titulo={feitos > 0 ? 'Fila concluída' : 'Nada para conferir agora'}
          texto={
            feitos > 0
              ? `Você conferiu ${String(feitos)} ${feitos === 1 ? 'produto' : 'produtos'} agora. Os casos novos aparecem aqui quando chegarem.`
              : `Nenhum produto espera a ${local === 'PRATELEIRA' ? 'prateleira' : 'contagem do estoque'} no momento.`
          }
        >
          <a className="dp-btn" href={voltar}>
            Voltar às divergências
          </a>
        </EstadoVazio>
      ) : (
        <article className="dp-produto" aria-labelledby="dp-produto-nome">
          <div className="dp-produto-cab dp-produto-cab-72">
            <FotoDoProduto url={atual.fotoProduto} nome={atual.produto} tamanho={72} />
            <div>
              <h2 className="dp-produto-nome" id="dp-produto-nome">
                {atual.produto}
              </h2>
              <Laboratorio nome={atual.fabricante} />
              <div className="dp-ids">
                {atual.codigo && (
                  <span>
                    Código HOS <b>{atual.codigo}</b>
                  </span>
                )}
                {atual.ean && (
                  <span>
                    EAN <b>{atual.ean}</b>
                  </span>
                )}
              </div>
            </div>
          </div>
          <dl className="dp-contexto">
            <div>
              <dt>Setor</dt>
              <dd>{atual.setor}</dd>
            </div>
            <div>
              <dt>Localização</dt>
              <dd>
                {atual.faixa
                  ? `B${String(atual.bloco)} · ${atual.faixa}`
                  : `Bloco ${String(atual.bloco)}`}
              </dd>
            </div>
            {local === 'PRATELEIRA' ? (
              <div>
                <dt>Contagem original</dt>
                <dd>
                  {atual.autoria ?? '—'}
                  {atual.contadoEm && (
                    <span className="dp-contexto-hora"> · {quandoBR(atual.contadoEm)}</span>
                  )}
                </dd>
              </div>
            ) : (
              <div>
                <dt>{porBusca ? 'Procurar no estoque' : 'Esperado no estoque'}</dt>
                <dd>
                  {porBusca
                    ? unidades(atual.alvo)
                    : atual.numeros?.esperadoNoEstoque != null
                      ? unidades(atual.numeros.esperadoNoEstoque)
                      : '—'}
                </dd>
              </div>
            )}
          </dl>

          <ResumoDaDivergencia
            caso={atual}
            investigacao={atual.id in investigacoes ? investigacoes[atual.id] : undefined}
            aoAtualizar={() => void investigar(atual.id, true)}
            local={local}
          />

          {!semVerificar ? (
            <form
              className="dp-pergunta"
              onSubmit={(e) => {
                e.preventDefault();
                void salvar();
              }}
            >
              <label htmlFor="dp-qtd">
                {porBusca
                  ? `Das ${String(atual.alvo)} que faltaram na reposição, quantas você achou no estoque?`
                  : PERGUNTA[local]}
              </label>
              <p className="dp-dica" id="dp-qtd-dica">
                {porBusca
                  ? `O HOS tem mais de 6: procure só as ${String(atual.alvo)} que o balconista não achou. De 0 a ${String(atual.alvo)}.`
                  : DICA[local]}
              </p>
              <input
                ref={campo}
                id="dp-qtd"
                className="dp-numero"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="off"
                maxLength={5}
                aria-describedby="dp-qtd-dica"
                value={valor}
                onChange={(e) => setValor(e.target.value.replace(/\D/g, ''))}
                placeholder="—"
              />
              {erro && (
                <div className="dp-alerta dp-alerta-erro" role="alert">
                  <Icone nome="warn" tamanho={16} />
                  {erro}
                </div>
              )}
              {incerto && (
                <div className="dp-alerta dp-alerta-aviso" role="alert">
                  <Icone nome="wifi" tamanho={16} />
                  Não deu para confirmar se salvou (conexão). Toque em salvar de novo — não duplica.
                </div>
              )}
              <div className="dp-conf-botoes">
                <button
                  type="submit"
                  className="dp-btn dp-btn-primario dp-btn-g"
                  disabled={!valido || enviando}
                >
                  {enviando ? 'Salvando…' : 'Salvar e próximo'}
                  {!enviando && <Icone nome="arrow" tamanho={18} traco={2} />}
                </button>
                {local === 'ESTOQUE' && (
                  <button
                    type="button"
                    className="dp-btn dp-btn-escuro"
                    disabled={enviando}
                    onClick={() => void maisDe12()}
                    title="Muita unidade no estoque: salva sem contar, como 'o produto estava lá'"
                  >
                    Tem mais de 12 no estoque
                  </button>
                )}
                <button
                  type="button"
                  className="dp-btn dp-btn-texto"
                  onClick={() => {
                    setErro(null);
                    setSemVerificar(true);
                  }}
                >
                  Não consegui verificar
                </button>
              </div>
            </form>
          ) : (
            <form
              className="dp-pergunta"
              onSubmit={(e) => {
                e.preventDefault();
                void registrarSemVerificar();
              }}
            >
              <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                <legend className="dp-dica" style={{ marginBottom: 10 }}>
                  O que impediu a contagem? O produto continua esperando — isto não vale como zero.
                </legend>
                <div className="dp-motivos">
                  {(Object.keys(ROTULO_SEM_VERIFICACAO) as MotivoSemVerificacao[]).map((m) => (
                    <label key={m} className="dp-opcao">
                      <input
                        type="radio"
                        name="dp-motivo"
                        checked={motivo === m}
                        onChange={() => setMotivo(m)}
                      />
                      {ROTULO_SEM_VERIFICACAO[m]}
                    </label>
                  ))}
                </div>
              </fieldset>
              {motivo === 'OUTRO' && (
                <input
                  className="dp-campo"
                  value={obs}
                  maxLength={200}
                  onChange={(e) => setObs(e.target.value)}
                  placeholder="O que aconteceu?"
                  aria-label="O que aconteceu"
                />
              )}
              {erro && (
                <div className="dp-alerta dp-alerta-erro" role="alert">
                  <Icone nome="warn" tamanho={16} />
                  {erro}
                </div>
              )}
              {incerto && (
                <div className="dp-alerta dp-alerta-aviso" role="alert">
                  <Icone nome="wifi" tamanho={16} />
                  Não deu para confirmar (conexão). Tente de novo — não duplica.
                </div>
              )}
              <div className="dp-conf-botoes">
                <button
                  type="submit"
                  className="dp-btn dp-btn-escuro dp-btn-g"
                  disabled={!motivo || enviando}
                >
                  {enviando ? 'Registrando…' : 'Registrar e ir para o próximo'}
                </button>
                <button
                  type="button"
                  className="dp-btn dp-btn-texto"
                  onClick={() => setSemVerificar(false)}
                >
                  Voltar para a contagem
                </button>
              </div>
            </form>
          )}
        </article>
      )}
    </section>
  );
}
